/*
 * Account And Contact Query Keys
 *
 * Features:
 * - Defines canonical list and detail cache namespaces
 * - Normalizes route, user, and team selectors before cache lookup
 *
 * Implementation:
 * - Positive integers identify entities and users
 * - Team queries additionally preserve -1 as the existing RLP selector
 * - Mutation boundaries reject invalid identifiers before network requests
 *
 * Changes:
 * - 2026-08-06: Added scoped keys for the account/contact Query pilot
 */

type NumericSelector = string | number | null | undefined;

export function normalizePositiveId(value: NumericSelector): number | null {
  if (value === null || value === undefined || value === '') return null;

  const normalized = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(normalized) && normalized > 0 ? normalized : null;
}

export function requirePositiveId(value: NumericSelector, fieldName: string): number {
  const normalized = normalizePositiveId(value);
  if (normalized === null) {
    throw new Error(`${fieldName} must be a positive integer`);
  }
  return normalized;
}

export function normalizeTeamId(value: NumericSelector): number | null {
  if (value === -1 || value === '-1') return -1;
  return normalizePositiveId(value);
}

export const accountKeys = {
  all: ['accounts'] as const,
  lists: () => [...accountKeys.all, 'list'] as const,
  global: () => [...accountKeys.lists(), 'global'] as const,
  team: (teamId: NumericSelector) => [...accountKeys.lists(), 'team', normalizeTeamId(teamId)] as const,
  user: (userId: NumericSelector) => [...accountKeys.lists(), 'user', normalizePositiveId(userId)] as const,
  details: () => [...accountKeys.all, 'detail'] as const,
  detail: (id: NumericSelector) => [...accountKeys.details(), normalizePositiveId(id)] as const,
};

export const contactKeys = {
  all: ['contacts'] as const,
  lists: () => [...contactKeys.all, 'list'] as const,
  team: (teamId: NumericSelector) => [...contactKeys.lists(), 'team', normalizeTeamId(teamId)] as const,
  user: (userId: NumericSelector) => [...contactKeys.lists(), 'user', normalizePositiveId(userId)] as const,
  details: () => [...contactKeys.all, 'detail'] as const,
  detail: (id: NumericSelector) => [...contactKeys.details(), normalizePositiveId(id)] as const,
};
